import axiosService from '../utils/axios';

export const sendCollabRequest = async (toUserId: string): Promise<void> => {
    await axiosService.post('/collaborations/request', { toUserId });
};

export const acceptCollabRequest = async (collabId: string | undefined): Promise<void> => {
    await axiosService.post(`/collaborations/accept/${collabId}`);
};

export interface CollabEntry {
    collabId: string;
    status: 'pending' | 'accepted' | 'ended';
    initiatedBy?: string;
    endedByUserId?: string | null;
    jamboardPinCreatorUserId?: string | null;
    startedFrom?: 'profile' | 'jamboard';
    jamboardPinId?: string | null;
    jamboardPinTitle?: string | null;
    jamboardPinDescription?: string | null;
    jamboardProjectLocation?: string | null;
    jamboardProjectRate?: string | null;
    jamboardProjectImage?: string | null;
    jamboardProjectImagePositionX?: number;
    jamboardProjectImagePositionY?: number;
    acceptedAt?: string | null;
    endedAt?: string | null;
    createdAt: string;
}

export const checkCollabStatus = async (toUserId: string): Promise<{
    status: 'none' | 'pending' | 'accepted',
    initiatedBy?: string,
    pendingCollabId?: string,
    collaborations: CollabEntry[]
}> => {
    try {
        const timestamp = new Date().getTime();
        const response = await axiosService.get(`/collaborations/status/${toUserId}?t=${timestamp}`);
        return {
            status: response.data.status,
            initiatedBy: response.data.initiatedBy,
            pendingCollabId: response.data.pendingCollabId,
            collaborations: response.data.collaborations || [],
        };
    } catch (error) {
        console.error('Error checking collaboration status:', error);
        return { status: 'none', collaborations: [] };
    }
};

// New function to check collaboration status directly by collabId
export const checkCollabStatusById = async (collabId: string): Promise<{status: 'none' | 'pending' | 'accepted'}> => {
    try {
        // Add a timestamp to prevent caching
        const timestamp = new Date().getTime();
        const response = await axiosService.get(`/collaborations/${collabId}/status?t=${timestamp}`);
        return {
            status: response.data.status || 'none'
        };
    } catch (error) {
        console.error('Error checking collaboration status by ID:', error);
        return { status: 'none' };
    }
};

export interface CollaboratorUser {
    _id: string;
    username: string;
    displayName?: string;
    profilePhoto?: string;
}

export interface CollaboratorGroup {
    otherUser: CollaboratorUser;
    collaborations: CollabEntry[];
    latestAcceptedAt: string;
}

// Backward-compat alias kept so nothing else breaks
export type Collaboration = CollaboratorGroup;

export const getUserCollaborations = async (): Promise<CollaboratorGroup[]> => {
    try {
        const response = await axiosService.get(`/collaborations/user?t=${Date.now()}`);
        return response.data.collaborations;
    } catch (error) {
        console.error('Error fetching user collaborations:', error);
        return [];
    }
};

export const endCollaboration = async (collabId: string): Promise<boolean> => {
    try {
        await axiosService.delete(`/collaborations/${collabId}`);
        return true;
    } catch (error) {
        console.error('Error ending collaboration:', error);
        return false;
    }
};

export const endAllCollaborations = async (partnerId: string): Promise<boolean> => {
    try {
        await axiosService.delete(`/collaborations/partner/${partnerId}`);
        return true;
    } catch (error) {
        console.error('Error ending all collaborations:', error);
        return false;
    }
};
